import { useRef, useState } from "react";
import { useDrag } from "@use-gesture/react";

/** Snap heights for bottom panels (chat widget drawer, bottom sheet, etc.). */
type SnapDrawerState =
  | "collapsed"
  | "half-expanded"
  | "expanded";

export const HALF_EXPANDED_VH = 48;
export const NAVBAR_HEIGHT_REM = 4;

const SNAP_EASING = "cubic-bezier(0.32, 0.72, 0, 1)";
/** Duration of the snap height transition; exported for callers that need to sync with the animation. */
const SNAP_DURATION_MS = 400;
const FLICK_VELOCITY_THRESHOLD = 0.5;
const DRAG_THRESHOLD_PX = 10;
const COLLAPSED_HEIGHT_FALLBACK_PX = 144; // 9rem

const SNAP_ORDER: readonly SnapDrawerState[] = [
  "collapsed",
  "half-expanded",
  "expanded",
];

const getSnapHeightPx = (
  state: SnapDrawerState,
  collapsedHeightPx: number,
): number => {
  switch (state) {
    case "collapsed":
      return collapsedHeightPx;
    case "half-expanded":
      return window.innerHeight * (HALF_EXPANDED_VH / 100);
    case "expanded":
      return window.innerHeight - NAVBAR_HEIGHT_REM * 16;
  }
};

const resolveSnapTarget = (
  currentHeightPx: number,
  velocity: number,
  direction: number,
  currentState: SnapDrawerState,
  collapsedHeightPx: number,
): SnapDrawerState => {
  const currentIndex = SNAP_ORDER.indexOf(currentState);

  if (velocity > FLICK_VELOCITY_THRESHOLD) {
    return direction > 0
      ? SNAP_ORDER[Math.max(currentIndex - 1, 0)]
      : SNAP_ORDER[Math.min(currentIndex + 1, SNAP_ORDER.length - 1)];
  }

  return SNAP_ORDER.reduce((nearest, state) => {
    const dist = Math.abs(
      currentHeightPx - getSnapHeightPx(state, collapsedHeightPx),
    );
    const nearestDist = Math.abs(
      currentHeightPx - getSnapHeightPx(nearest, collapsedHeightPx),
    );
    return dist < nearestDist ? state : nearest;
  });
};

export const useSnapDrawer = (
  drawerState: SnapDrawerState,
  onDrawerStateChange: (state: SnapDrawerState) => void,
) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const startHeightRef = useRef(0);
  const collapsedHeightRef = useRef(COLLAPSED_HEIGHT_FALLBACK_PX);
  const [isDragging, setIsDragging] = useState(false);

  const drawerStateRef = useRef(drawerState);
  drawerStateRef.current = drawerState;
  const onDrawerStateChangeRef = useRef(onDrawerStateChange);
  onDrawerStateChangeRef.current = onDrawerStateChange;

  const bind = useDrag(
    ({
      movement: [, my],
      velocity: [, vy],
      direction: [, dy],
      last,
      first,
    }) => {
      const panel = panelRef.current;
      if (!panel) return;

      if (first) {
        const cssVar = getComputedStyle(panel)
          .getPropertyValue("--collapsed-h")
          .trim();
        collapsedHeightRef.current = cssVar
          ? parseFloat(cssVar)
          : COLLAPSED_HEIGHT_FALLBACK_PX;
        startHeightRef.current = panel.offsetHeight;
        panel.style.transition = "none";
        setIsDragging(true);
      }

      if (!last) {
        panel.style.height = `${Math.max(
          startHeightRef.current - my,
          collapsedHeightRef.current,
        )}px`;
      } else {
        const targetState = resolveSnapTarget(
          panel.offsetHeight,
          vy,
          dy,
          drawerStateRef.current,
          collapsedHeightRef.current,
        );
        const targetPx = getSnapHeightPx(
          targetState,
          collapsedHeightRef.current,
        );

        panel.style.transition = `height ${SNAP_DURATION_MS}ms ${SNAP_EASING}`;
        panel.style.height = `${targetPx}px`;

        setIsDragging(false);
        onDrawerStateChangeRef.current(targetState);

        setTimeout(() => {
          if (!panelRef.current) return;
          panelRef.current.style.transition = "";
          panelRef.current.style.height = "";
        }, SNAP_DURATION_MS + 50);
      }
    },
    {
      axis: "y",
      pointer: { capture: true },
      filterTaps: true,
      threshold: DRAG_THRESHOLD_PX,
    },
  );

  return { panelRef, bind, isDragging };
};
